import { getShapeAabb } from "./bounds";
import { type DocumentState, moveShapes } from "./operations";

export type AlignKind =
  | "left"
  | "centerX"
  | "right"
  | "top"
  | "centerY"
  | "bottom";

export type DistributeAxis = "x" | "y";

/**
 * Lines the shapes up on one edge or centre line of their common bounding
 * box (rotation-aware bounds). Locked shapes stay where they are but still
 * count towards the box, so aligning to a locked wall works.
 */
export const alignShapes = (
  document: DocumentState,
  ids: string[],
  kind: AlignKind
): DocumentState => {
  const entries = ids
    .map((id) => {
      const shape = document.shapes[id];
      return shape ? { id, box: getShapeAabb(shape) } : null;
    })
    .filter((entry): entry is NonNullable<typeof entry> => !!entry);
  if (entries.length < 2) return document;
  const left = Math.min(...entries.map((entry) => entry.box.x));
  const right = Math.max(
    ...entries.map((entry) => entry.box.x + entry.box.width)
  );
  const top = Math.min(...entries.map((entry) => entry.box.y));
  const bottom = Math.max(
    ...entries.map((entry) => entry.box.y + entry.box.height)
  );
  let next = document;
  for (const { id, box } of entries) {
    let dx = 0;
    let dy = 0;
    switch (kind) {
      case "left":
        dx = left - box.x;
        break;
      case "centerX":
        dx = (left + right) / 2 - (box.x + box.width / 2);
        break;
      case "right":
        dx = right - (box.x + box.width);
        break;
      case "top":
        dy = top - box.y;
        break;
      case "centerY":
        dy = (top + bottom) / 2 - (box.y + box.height / 2);
        break;
      case "bottom":
        dy = bottom - (box.y + box.height);
        break;
    }
    next = moveShapes(next, [id], Math.round(dx), Math.round(dy));
  }
  return next;
};

/**
 * Spaces the shapes evenly along an axis: the outermost two stay put and
 * the gaps between neighbouring edges become equal. Needs three shapes.
 */
export const distributeShapes = (
  document: DocumentState,
  ids: string[],
  axis: DistributeAxis
): DocumentState => {
  const entries = ids
    .map((id) => {
      const shape = document.shapes[id];
      return shape ? { id, box: getShapeAabb(shape) } : null;
    })
    .filter((entry): entry is NonNullable<typeof entry> => !!entry);
  if (entries.length < 3) return document;
  const start = (box: { x: number; y: number }) =>
    axis === "x" ? box.x : box.y;
  const size = (box: { width: number; height: number }) =>
    axis === "x" ? box.width : box.height;
  const sorted = [...entries].sort(
    (a, b) => start(a.box) + size(a.box) / 2 - (start(b.box) + size(b.box) / 2)
  );
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  const span = start(last.box) + size(last.box) - start(first.box);
  const occupied = sorted.reduce((sum, entry) => sum + size(entry.box), 0);
  const gap = (span - occupied) / (sorted.length - 1);
  let next = document;
  let cursor = start(first.box) + size(first.box) + gap;
  for (const entry of sorted.slice(1, -1)) {
    const delta = Math.round(cursor - start(entry.box));
    next = moveShapes(
      next,
      [entry.id],
      axis === "x" ? delta : 0,
      axis === "y" ? delta : 0
    );
    cursor += size(entry.box) + gap;
  }
  return next;
};
